import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import SettingsSection from '../../SettingsSection'
import Add from '../../Controls/Add'
import NoPermissionContainer from '@SonarCX/components/molecules/NoPermissionContainer'
import DraggableSetting from '../DraggableSetting'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import {
  createType,
  deleteType,
  fetchTypes,
  patchTypes,
  selectTaskType,
  updateType
} from '@SonarCX/slices/taskType'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useStats } from '@SonarCX/providers/StatsProvider'
import { doAlphabeticalSort, doDragDrop } from '../DraggableSetting/helpers'

import useStyles from './styles'

const getCount = (type, taskStats) => {
  if (!taskStats) {
    return 0
  }

  const { types } = taskStats
  const count = types.find(t => t.id === type.id)?.value
  return count ?? 0
}

const getTypeObj = (type, classes, stats) => {
  const count = getCount(type, stats)

  const dispName = (
    <div className={classes.name}>
      {type.name} <div>({count})</div>
    </div>
  )

  return {
    ...type,
    dispName,
    count
  }
}

const TypeSettings = () => {
  const dispatch = useAppDispatch()
  const {
    typesValues: types,
    loaded,
    loading,
    error
  } = useAppSelector(selectTaskType)
  const { globalTasks, globalLoaded } = useStats()
  const hasPermission = useAccess()
  const [saving, setSaving] = React.useState(false)

  const classes = useStyles()

  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchTypes())
    }
  }, [loaded, loading, dispatch])

  const handleAddType = React.useCallback(
    async name => {
      try {
        setSaving(true)
        await dispatch(createType(name))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      } finally {
        setSaving(false)
      }
    },
    [dispatch]
  )

  const handleDeleteType = React.useCallback(
    obj => {
      try {
        dispatch(deleteType(obj.id))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleEditType = React.useCallback(
    async (obj, name) => {
      try {
        await dispatch(updateType({ ...obj, name }))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleDragDrop = (
    sourceID: string,
    targetID: string,
    after?: boolean
  ) => {
    const toUpdate = doDragDrop(types, sourceID, targetID, after)

    dispatch(patchTypes(toUpdate))
  }

  const handleSort = React.useCallback(
    (ascending: boolean) => {
      dispatch(patchTypes(doAlphabeticalSort(ascending, types)))
    },
    [types, dispatch]
  )

  if (!hasPermission(PERMS.TasksAdmin)) {
    return null
  }

  return (
    <NoPermissionContainer error={error} skipAgentPage>
      <SettingsSection
        title='Types'
        loaded={loaded && globalLoaded}
        sort={handleSort}
      >
        {types.map((type, i) => {
          const { count, ...obj } = getTypeObj(type, classes, globalTasks)

          return (
            <DraggableSetting
              key={type.id}
              obj={obj}
              objType='type'
              superType='task'
              handleDelete={handleDeleteType}
              handleEdit={handleEditType}
              isLast={i === types.length - 1}
              index={i}
              count={count}
              onDragDrop={handleDragDrop}
            />
          )
        })}

        <Add
          superType='task'
          type='type'
          handleAdd={handleAddType}
          saving={saving}
        />
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default TypeSettings
